// Owner-only screen for the onboarding agent door built on the Worker by
// onb-4 (#1238). This page is presentation only: the Worker enforces the
// owner human session on every route. A bearer returned by Add is shown once,
// inside the card that holds the Add button, and is never written to an
// attribute, the URL, storage or a log.

import { AdminApiError, type AdminClient } from "../api.js";
import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import { clear, h } from "../dom.js";
import type {
  AgentIdentity,
  AgentIdentityCreateResponse,
  Company,
  StaffRole,
} from "../types.js";
import { disclosure, distinctControlNames, pageHeader } from "../ui/components.js";
import { advice } from "../ui/capabilities.js";
import { dateText } from "../ui/display-words.js";
import * as face from "../ui/face.js";
import { readerRole } from "../ui/facts.js";
import { failureCopy, outcomeSentence } from "../ui/failure.js";
import { navLabel } from "../ui/sentences.js";
import { forgetPossiblySent, rememberPossiblySent, runWrite, type WriteOutcome } from "../ui/write.js";
import { appendReceipt, renderReceiptCard, renderReceiptFailure } from "../ui/receipt_card.js";

/** The account manager is told who does it, in the decided capability words. */
export const AGENT_ACCESS_DENIED_COPY = advice("view_agent_identities", "account_manager");

export const AGENT_BEARER_WARNING =
  "Put this and the service token's Client Secret only in the agent's secret settings. Never in a file, the repo or a chat. It is not shown again.";

export const APPROVAL_ID_REQUIRED_COPY = "Enter the ticket id from your agent.";
export const APPROVAL_ID_TOO_LONG_COPY =
  "The ticket id is too long. A ticket id is 1 to 128 characters.";
export const APPROVAL_SESSION_COPY =
  "The approval page needs your own signed-in owner session. A ticket id on its own is not authority.";

export interface AgentAccessPageOptions {
  client: AdminClient;
  root: HTMLElement;
  role: StaffRole;
  /** Active companies the signed-in owner already has (no extra list call). */
  companies: readonly Company[];
  /** Open the existing `approval` route with this ticket id (no API call). */
  openApproval: (approvalId: string) => void;
  isCurrent?: () => boolean;
}

/** Orchestrator wording (rd r12 F1, R3): an Add whose Client ID is taken. */
export const AGENT_ALREADY_EXISTS = "An agent with this Client ID already exists.";

export function revokeConfirmText(label: string): string {
  return `Revoke ${label}? The agent stops working at once. A revoked identity's Client ID cannot be added again; you would create a new service token.`;
}

/**
 * A failed write: the decided sentence on the face and the server's own words
 * only behind Show details. `unchanged` is the caller's honest line: a lost
 * response or a 5xx may have landed, so it is never "Nothing changed." here.
 */
function failureReceipt(error: unknown, role: StaffRole, unchanged: string): HTMLElement {
  const copy = failureCopy(error, { role, context: "write" });
  const receipt = renderReceiptFailure(copy.sentence, unchanged);
  if (copy.detail !== null) {
    receipt.append(disclosure("Show details", h("p", {}, [copy.detail])));
  }
  return receipt;
}

/**
 * An Add (a credential issue) failure. An unknown answer may have minted a
 * bearer, so it reads the possibly-sent sentence, never "refresh": a reload
 * forgets the possibly-sent mark (round 2 M1). The caller adds the page's own
 * check as the way out.
 */
function issueFailureReceipt(outcome: WriteOutcome, role: StaffRole): HTMLElement {
  if (outcome.kind === "conflict") {
    // The Worker's only 409 on this route is "Agent identity already exists"
    // (`onboarding_agent_routes.ts`, the UNIQUE common name). A retry of a
    // possibly-sent Add is blocked before it is sent, so a 409 here is a name
    // that was already taken: say that once, never the generic "This changed
    // while you were working", which also filled the line below it (rd r12 F1,
    // R3). Nothing was created, so the line below says nothing changed.
    const receipt = renderReceiptFailure(
      AGENT_ALREADY_EXISTS,
      outcomeSentence({ kind: "refused", status: 0 }, role),
    );
    const detail =
      outcome.error === undefined ? null : failureCopy(outcome.error, { role, context: "write" }).detail;
    if (detail !== null) receipt.append(disclosure("Show details", h("p", {}, [detail])));
    return receipt;
  }
  if (outcome.kind === "refused") {
    return failureReceipt(outcome.error, role, outcomeSentence(outcome, role));
  }
  // `unknown` (or a landed run whose value never reached the page): the request
  // may already be applied.
  const sentence = outcomeSentence({ kind: "unknown", blocked: true }, role);
  const receipt = renderReceiptFailure(sentence, sentence);
  const error = outcome.kind === "landed" ? undefined : outcome.error;
  if (error !== undefined) {
    const copy = failureCopy(error, { role, context: "write" });
    if (copy.detail !== null) {
      receipt.append(disclosure("Show details", h("p", {}, [copy.detail])));
    }
  }
  return receipt;
}

function formatWhen(iso: string): string {
  const shown = dateText(iso);
  return shown === "" ? "Date not known" : shown;
}

function identityStatus(identity: AgentIdentity): string {
  return identity.revokedAt === null
    ? "active"
    : `revoked ${formatWhen(identity.revokedAt)}`;
}

function renderIdentityRow(identity: AgentIdentity, opts: {
  client: AdminClient;
  role: StaffRole;
  stillCurrent: () => boolean;
  /**
   * Called after a revoke succeeds, with this identity's own row. The bearer the
   * owner was shown when an identity was added lives in the Add card, not here,
   * and must not stay on screen after THAT identity stops working — but
   * revoking some other identity must leave it alone (rd round 10 review,
   * MUST-FIX 1). Rows are identified by the element itself: the identity key
   * never leaves the page as an argument.
   */
  onRevoked?: (row: HTMLElement) => void;
}): HTMLLIElement {
  const revoked = identity.revokedAt !== null;
  const status = h("span", {
    class: "muted",
    "data-role": "agent-identity-status",
  }, [identityStatus(identity)]);

  const receiptHost = h("div", { "data-role": "agent-identity-receipt" });
  const confirmHost = h("div", { "data-role": "agent-identity-confirm", hidden: "true" });

  const row = h("li", {
    class: revoked ? "agent-identity revoked" : "agent-identity",
    "data-role": "agent-identity",
    "data-common-name": identity.commonName,
    "data-status": revoked ? "revoked" : "active",
  }, [
    h("div", { class: "agent-identity-main" }, [
      h("strong", {}, [amVisibleDisplayText(identity.label)]),
      h("span", { class: "muted" }, [` · added ${formatWhen(identity.createdAt)} · `, status]),
      disclosure("Show details", h("p", {}, [amVisibleDisplayText(identity.commonName)])),
    ]),
  ]);

  if (revoked) {
    row.append(confirmHost, receiptHost);
    return row;
  }

  const revoke = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "revoke-agent-identity",
      "aria-label": `Revoke ${identity.label}`,
    },
    ["Revoke"],
  );

  revoke.addEventListener("click", () => {
    clear(confirmHost);
    confirmHost.hidden = false;
    confirmHost.removeAttribute("hidden");
    confirmHost.append(
      h("p", { class: "muted", "data-role": "agent-identity-confirm-copy" }, [
        revokeConfirmText(identity.label),
      ]),
    );
    const confirm = h(
      "button",
      {
        type: "button",
        class: "btn btn-primary",
        "data-action": "confirm-revoke-agent-identity",
      },
      ["Confirm revoke"],
    );
    const cancel = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-action": "cancel-revoke-agent-identity",
      },
      ["Cancel"],
    );
    cancel.addEventListener("click", () => {
      clear(confirmHost);
      confirmHost.hidden = true;
      confirmHost.setAttribute("hidden", "true");
    });
    confirm.addEventListener("click", () => {
      void (async () => {
        const held: { revokedAt: string | null } = { revokedAt: null };
        const outcome = await runWrite({
          key: `agent-identity-revoke:${amVisibleDisplayText(identity.commonName)}`,
          scope: row,
          gate: () => null,
          send: () => opts.client.revokeAgentIdentity({ commonName: identity.commonName }),
          afterLanded: (value) => {
            held.revokedAt = value.revokedAt;
          },
          stillCurrent: opts.stillCurrent,
        });
        if (outcome.kind === "landed") {
          if (!opts.stillCurrent()) return;
          // The credential is dead: the box that showed it goes with it, if
          // that box is THIS identity's.
          opts.onRevoked?.(row);
          status.textContent = `revoked ${formatWhen(held.revokedAt ?? new Date().toISOString())}`;
          row.setAttribute("data-status", "revoked");
          row.classList.add("revoked");
          revoke.remove();
          clear(confirmHost);
          confirmHost.hidden = true;
          confirmHost.setAttribute("hidden", "true");
          appendReceipt(
            receiptHost,
            renderReceiptCard({
              happened: `Revoked ${amVisibleDisplayText(identity.label)} (${amVisibleDisplayText(identity.commonName)}).`,
              unchanged: "The agent stops working at once.",
              next: {
                who: "Owner",
                text: "Create a new service token and add a new identity if the agent must return.",
              },
            }),
          );
          return;
        }
        if (!opts.stillCurrent()) return;
        clear(receiptHost);
        appendReceipt(
          receiptHost,
          failureReceipt(
            outcome.error,
            opts.role,
            outcomeSentence(outcome, opts.role),
          ),
        );
        confirm.disabled = false;
        revoke.disabled = false;
      })();
    });
    confirmHost.append(h("div", { class: "form-row" }, [confirm, cancel]));
  });

  row.append(revoke, confirmHost, receiptHost);
  return row;
}

/**
 * Owner-only Agent access page: list, add and revoke named service identities.
 * Nothing on this page invents an "added by" value: the contract does not
 * return one.
 */
export async function renderAgentAccessPage(opts: AgentAccessPageOptions): Promise<void> {
  const { client, root } = opts;
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  clear(root);
  const page = h("div", { class: "settings-stack" });
  root.append(page);

  page.append(
    pageHeader(
      navLabel("agent-access"),
      "Add a named agent, then open a ticket when the agent asks you to approve something.",
    ),
  );

  if (readerRole(opts.role) !== "owner") {
    page.append(
      h("p", {
        class: "empty",
        role: "status",
        "data-role": "agent-access-denied",
      }, [AGENT_ACCESS_DENIED_COPY]),
    );
    return;
  }

  const listHost = h("div", { "data-role": "agent-access-list-host" });
  page.append(h("h2", {}, ["Named identities"]), listHost);
  listHost.append(h("p", { class: "muted", "data-role": "agent-identity-loading" }, [
    "Loading identities…",
  ]));

  /**
   * True while an Add is in flight. A list Retry re-renders the whole page, so
   * pressing it then would throw away the one-time bearer the Add is about to
   * show. Retry is disabled for the duration, and this flag is the second line
   * of defence for a programmatic click (rd round 11 review, MEDIUM 6).
   */
  let addPending = false;
  const setListRetryDisabled = (disabled: boolean): void => {
    const retry = listHost.querySelector<HTMLButtonElement>('[data-action="retry"]');
    if (retry !== null) retry.disabled = disabled;
  };

  let identities: AgentIdentity[] = [];
  try {
    const body = await client.listAgentIdentities();
    identities = body.identities;
  } catch (error) {
    if (!stillCurrent()) return;
    clear(listHost);
    const retry = h(
      "button",
      { type: "button", class: "btn btn-primary", "data-action": "retry", "aria-label": "Retry" },
      ["Try again"],
    );
    retry.addEventListener("click", () => {
      if (addPending) return;
      void renderAgentAccessPage(opts);
    });
    listHost.append(
      h("div", { class: "error-state", role: "alert", "data-role": "agent-identity-list-error" }, [
        h("p", {}, ["We could not load agent identities. Try again."]),
        disclosure(
          "Show details",
          h("p", {}, [failureCopy(error, { role: readerRole(opts.role), context: "read" }).detail ?? "Request failed"]),
        ),
        retry,
      ]),
    );
  }
  if (!stillCurrent()) return;

  /** The identities this page held before any Add (round 2 M4). */
  const knownCommonNames = new Set(identities.map((identity) => identity.commonName));

  const addReceiptHost = h("div", { "data-role": "agent-access-add-receipt" });
  /**
   * The row whose one-time bearer the Add card is showing, or null. The box is
   * cleared only when that identity is the one revoked: revoking an older
   * identity used to wipe a bearer the person had not copied yet while the
   * identity it belonged to stayed active (rd round 10 review, MUST-FIX 1).
   */
  let shownBearerRow: HTMLElement | null = null;
  const clearShownBearer = (): void => {
    clear(addReceiptHost);
    shownBearerRow = null;
  };
  const onIdentityRevoked = (row: HTMLElement): void => {
    if (shownBearerRow !== row) return;
    clearShownBearer();
  };

  const list = h("ul", { class: "token-list", "data-role": "agent-identity-list" });
  if (identities.length === 0) {
    list.append(
      h("li", { class: "empty", "data-role": "agent-identity-empty" }, [
        "No agents yet. Add one below. You need the access client id and a short name.",
      ]),
    );
  }
  for (const identity of identities) {
    list.append(
      renderIdentityRow(identity, { client, role: readerRole(opts.role), stillCurrent, onRevoked: onIdentityRevoked }),
    );
  }
  // Two identities with one label read identically: tell them apart by
  // position, never by common name (round 3).
  const nameRevokes = (): void => {
    distinctControlNames(list, '[data-action="revoke-agent-identity"]');
  };
  nameRevokes();
  if (identities.length > 0 || listHost.querySelector('[data-role="agent-identity-list-error"]') === null) {
    clear(listHost);
    listHost.append(list);
  } else {
    // Keep the load error visible; the add card below still works.
  }

  const addCard = h("section", {
    class: "card agent-access-add",
    "data-role": "agent-access-add",
  });
  const commonName = h("input", {
    type: "text",
    name: "commonName",
    placeholder: "Cloudflare Access Client ID",
    "aria-label": "Cloudflare Access Client ID",
    autocomplete: "off",
  });
  const label = h("input", {
    type: "text",
    name: "label",
    placeholder: "Label (for example: Onboarding agent)",
    "aria-label": "Label",
    autocomplete: "off",
  });
  // The option's value is the slug the Worker takes; its text is the name a
  // person reads, like every other company picker (rd round 11 review, HIGH 2).
  const directory = face.createCompanyDirectory(
    opts.companies.map((row) => ({ slug: row.slug, displayName: row.displayName })),
    { complete: true },
  );
  const company = h("select", { "aria-label": "Company" });
  company.append(h("option", { value: "", selected: "true" }, ["Choose a company"]));
  for (const row of opts.companies) {
    company.append(
      h("option", { value: row.slug }, [face.company(row.slug, directory, "sentence")]),
    );
  }
  company.value = "";

  const addButton = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "add-agent-identity",
    },
    ["Add identity"],
  );
  const receiptHost = addReceiptHost;

  /**
   * The way out of a blocked Add: read the page's own list. A new identity is
   * adopted (its bearer is gone for good) and the memory is cleared; no new
   * identity clears the memory so a deliberate retry is possible (round 2 M4).
   */
  const offerIdentityCheck = (key: string, common: string): HTMLElement => {
    const check = h(
      "button",
      {
        type: "button",
        class: "btn btn-secondary",
        "data-action": "check-agent-identities",
      },
      ["Check identities"],
    );
    check.addEventListener("click", () => {
      void (async () => {
        check.disabled = true;
        let found: AgentIdentity | undefined;
        try {
          const body = await client.listAgentIdentities();
          found = body.identities.find(
            (identity) =>
              identity.commonName === common && !knownCommonNames.has(identity.commonName),
          );
        } catch (error) {
          // The read failed. The control stays: it is the only way out of the
          // blocked state, so a failed read must not take the way out with it
          // (round 3).
          const receipt = renderReceiptFailure(
            failureCopy(error, { role: readerRole(opts.role), context: "read" }).sentence,
            "The list could not be read, so an identity added by that request still needs checking.",
          );
          check.disabled = false;
          receipt.append(check);
          clear(receiptHost);
          appendReceipt(receiptHost, receipt);
          return;
        }
        forgetPossiblySent(key);
        clear(receiptHost);
        if (found === undefined) {
          // A read that found nothing is a read, not a write: never "Nothing
          // changed." (round 3).
          appendReceipt(
            receiptHost,
            renderReceiptFailure(
              "I checked: no new identity was created.",
              "Press Add identity again when you are ready.",
            ),
          );
          return;
        }
        knownCommonNames.add(found.commonName);
        // Adopt what exists: never claim a bearer this page can show.
        appendReceipt(
          receiptHost,
          renderReceiptFailure(
            "The identity was created, but its token cannot be shown again.",
            "It exists in the list below. Revoke it and add it again if the agent needs the token.",
          ),
        );
        const empty = list.querySelector('[data-role="agent-identity-empty"]');
        if (empty !== null) empty.remove();
        list.prepend(
          renderIdentityRow(found, {
            client,
            role: readerRole(opts.role),
            stillCurrent,
            onRevoked: onIdentityRevoked,
          }),
        );
        nameRevokes();
      })();
    });
    return check;
  };

  addButton.addEventListener("click", () => {
    void (async () => {
      const common = commonName.value.trim();
      const labelText = label.value.trim();
      const companySlug = company.value;
      if (common.length === 0 || labelText.length === 0 || companySlug.length === 0) {
        clear(receiptHost);
        shownBearerRow = null;
        appendReceipt(
          receiptHost,
          renderReceiptFailure(
            "Enter the Cloudflare Access Client ID, a label and a company.",
            outcomeSentence({ kind: "refused", status: 0 }, readerRole(opts.role)),
          ),
        );
        return;
      }
      // The stable key + possibly-sent memory: a lost answer must never create
      // a second identity (and a second bearer).
      const key = `agent-identity:${companySlug}/${common}`;
      const held: { created: AgentIdentityCreateResponse | null } = { created: null };
      /**
       * The status the Add itself was answered with. A 409 is the Worker saying
       * the name is already taken, so the read-back below must not then find
       * that name and call it this press's identity: with the list unread
       * (`knownCommonNames` empty) it did, and the page said "The identity was
       * created" for an Add the Worker refused (rd r12 F1, R3).
       */
      const sent: { status: number | null } = { status: null };
      addPending = true;
      setListRetryDisabled(true);
      const outcome = await runWrite({
        key,
        scope: addCard,
        gate: () => null,
        send: async () => {
          try {
            return await client.createAgentIdentity({
              commonName: common,
              label: labelText,
              company: companySlug,
            });
          } catch (error) {
            sent.status = error instanceof AdminApiError ? error.status : null;
            throw error;
          }
        },
        // The identity's name is the natural idempotency key the Worker takes:
        // a lost answer is settled by reading the list for a name this page did
        // not already hold (round 2 M4: a pre-existing identity is not new).
        readBack: async () => {
          if (sent.status === 409) return false;
          const body = await client.listAgentIdentities();
          return body.identities.some(
            (identity) =>
              identity.commonName === common && !knownCommonNames.has(identity.commonName),
          );
        },
        afterLanded: (value) => {
          held.created = value;
        },
        stillCurrent,
        blockWhenPossiblySent: true,
      });
      addPending = false;
      setListRetryDisabled(false);
      if (outcome.kind === "landed" && !stillCurrent()) {
        // The bearer never reached the page, and the visit is gone. The next
        // visit must take the check path, never offer a fresh Add (M2).
        rememberPossiblySent(key);
        return;
      }
      if (!stillCurrent()) return;
      addButton.disabled = false;
      if (outcome.kind !== "landed") {
        clear(receiptHost);
        // The bearer box is gone: nothing is on screen for a later revoke to
        // clear.
        shownBearerRow = null;
        const receipt = issueFailureReceipt(outcome, readerRole(opts.role));
        if (outcome.kind === "unknown") receipt.append(offerIdentityCheck(key, common));
        appendReceipt(receiptHost, receipt);
        return;
      }
      if (held.created === null) {
        // The read-back confirmed an identity, but its bearer never reached
        // this page. Never claim an identity without saying the token is gone.
        clear(receiptHost);
        shownBearerRow = null;
        appendReceipt(
          receiptHost,
          renderReceiptFailure(
            "The identity was created, but its token cannot be shown again.",
            "It exists in the list below. Revoke it and add it again if the agent needs the token.",
          ),
        );
        return;
      }
      const added: AgentIdentityCreateResponse = held.created;
      // The page reported this identity: it is no longer "new" to this page, so
      // a later Add of the same name that the Worker refuses (409) cannot be
      // read back as an identity this press created (round 3 N1; `tokens.ts`
      // does the same with `knownIds`).
      knownCommonNames.add(added.identity.commonName);
      const receipt = renderReceiptCard(
        {
          happened: `Added ${amVisibleDisplayText(added.identity.label)} (${amVisibleDisplayText(added.identity.commonName)}).`,
          unchanged: "Nothing else changed.",
          next: null,
        },
        {
          oneTime: {
            label: `Bearer for ${added.identity.label}`,
            value: added.token,
          },
          oneTimeRole: "agent-bearer",
        },
      );
      receipt.append(
        h("p", { class: "muted", "data-role": "agent-bearer-warning" }, [
          AGENT_BEARER_WARNING,
        ]),
      );
      clear(receiptHost);
      appendReceipt(receiptHost, receipt);

      if (!list.isConnected) {
        clear(listHost);
        listHost.append(list);
      }
      const empty = list.querySelector('[data-role="agent-identity-empty"]');
      if (empty !== null) empty.remove();
      const addedRow = renderIdentityRow(added.identity, {
        client,
        role: readerRole(opts.role),
        stillCurrent,
        onRevoked: onIdentityRevoked,
      });
      list.prepend(addedRow);
      nameRevokes();
      // The bearer above belongs to this row and to no other.
      shownBearerRow = addedRow;
      commonName.value = "";
      label.value = "";
      company.value = "";
    })();
  });

  addCard.append(
    h("h2", {}, ["Add an identity"]),
    h("p", { class: "muted" }, [
      "The company anchors the credential. It does not grant access to that company's books.",
    ]),
    h("div", { class: "form-row" }, [
      h("label", {}, ["Access client id", commonName]),
      h("label", {}, ["Label", label]),
      h("label", {}, ["Company", company]),
      addButton,
    ]),
    receiptHost,
  );
  page.append(addCard);

  // No URL router in this console: the owner reaches the existing `approval`
  // route by typing the ticket id the agent gave them. This section only
  // navigates; the approval page owns its read, approve and reveal calls.
  const openApprovalCard = h("section", {
    class: "card agent-access-open-approval",
    "data-role": "agent-access-open-approval",
  });

  const ticketId = h("input", {
    type: "text",
    name: "approvalId",
    placeholder: "Ticket id from your agent",
    "aria-label": "Ticket id from your agent",
    autocomplete: "off",
  });
  const openApproval = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "open-approval-reveal",
    },
    ["Open approval"],
  );
  const openApprovalError = h("div", { "data-role": "open-approval-error" });
  const showOpenApprovalError = (message: string): void => {
    clear(openApprovalError);
    openApprovalError.append(
      h("p", { class: "banner banner-err", role: "alert" }, [message]),
    );
  };
  openApproval.addEventListener("click", () => {
    const value = ticketId.value.trim();
    if (value.length === 0) {
      showOpenApprovalError(APPROVAL_ID_REQUIRED_COPY);
      return;
    }
    if (value.length > 128) {
      showOpenApprovalError(APPROVAL_ID_TOO_LONG_COPY);
      return;
    }
    clear(openApprovalError);
    opts.openApproval(value);
    // The id is navigation input, not page state, and goes no further.
    ticketId.value = "";
  });
  openApprovalCard.append(
    h("h2", {}, ["Open an approval"]),
    h("p", { class: "muted", "data-role": "open-approval-session" }, [
      APPROVAL_SESSION_COPY,
    ]),
    h("div", { class: "form-row" }, [
      h("label", {}, ["Ticket id from your agent", ticketId]),
      openApproval,
    ]),
    openApprovalError,
  );
  page.append(openApprovalCard);
}
